import {
  useState,
  useEffect,
  createContext,
  useContext,
  useCallback,
} from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { createPortal } from "react-dom";
import {
  Dialog,
  DialogOverlay,
  DialogPortal,
} from "@/designs/shadcn/ui/dialog";

export const ModalCloseContext = createContext<VoidFunction | null>(null);

let modalOpenCount = 0;
let prevHtmlOverflow = "";
let prevBodyOverflow = "";
let prevHtmlTouchAction = "";
let prevBodyTouchAction = "";

const Modal = ({
  children,
  onClose,
  stickyFooter,
  showCloseButton = false,
  full = false,
  title = "Modal",
}: {
  children: React.ReactNode;
  onClose: VoidFunction;
  stickyFooter?: React.ReactNode | ((close: VoidFunction) => React.ReactNode);
  showCloseButton?: boolean;
  full?: boolean;
  title?: string;
}) => {
  const [isClosing, setIsClosing] = useState(false);

  const handleClose = useCallback(() => {
    if (isClosing) return;
    setIsClosing(true);
    setTimeout(() => {
      onClose();
    }, 300);
  }, [isClosing, onClose]);

  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    if (modalOpenCount === 0) {
      prevHtmlOverflow = html.style.overflow;
      prevBodyOverflow = body.style.overflow;
      prevHtmlTouchAction = html.style.touchAction;
      prevBodyTouchAction = body.style.touchAction;
      html.style.overflow = "hidden";
      body.style.overflow = "hidden";
      html.style.touchAction = "none";
      body.style.touchAction = "none";
    }
    modalOpenCount += 1;
    return () => {
      modalOpenCount -= 1;
      if (modalOpenCount === 0) {
        html.style.overflow = prevHtmlOverflow;
        body.style.overflow = prevBodyOverflow;
        html.style.touchAction = prevHtmlTouchAction;
        body.style.touchAction = prevBodyTouchAction;
      }
    };
  }, []);

  const modalContent = (
    <ModalCloseContext.Provider value={handleClose}>
      <Dialog
        open
        onOpenChange={(openState) => {
          if (!openState) handleClose();
        }}
      >
        <DialogPortal>
          <DialogOverlay
            // Important for nested modals: overlay and content must share the same z-index.
            // Radix portals mount in DOM order; the later (nested) modal will render on top.
            className={`z-[200] bg-[rgba(0,0,0,0.4)] transition-opacity duration-300 ease-out data-[state=open]:animate-none data-[state=closed]:animate-none ${
              isClosing ? "opacity-0" : "opacity-100"
            }`}
          />
          <DialogPrimitive.Content
            className="fixed inset-0 z-[200] flex items-end outline-none"
            onEscapeKeyDown={(e) => {
              e.preventDefault();
              handleClose();
            }}
            // This dialog is rendered conditionally by parents; prevent Radix from
            // attempting to control unmount timing (we handle close animation).
            onPointerDownOutside={(e) => e.preventDefault()}
            onInteractOutside={(e) => e.preventDefault()}
          >
            <DialogPrimitive.Title className="sr-only">
              {title}
            </DialogPrimitive.Title>
            <div className="fixed inset-0 flex items-end" onClick={handleClose}>
              <div
                className={`w-full md:max-w-[480px] mx-auto bg-white ${
                  full ? "h-screen" : "rounded-t-[32px] max-h-[85vh]"
                } overflow-hidden transition-transform duration-300 ease-out relative will-change-transform flex flex-col ${
                  isClosing
                    ? "transform translate-y-full"
                    : "transform translate-y-0 animate-in slide-in-from-bottom"
                }`}
                onClick={(e) => e.stopPropagation()}
              >
                <div
                  className="flex-1 min-h-0 overflow-x-hidden overflow-y-auto"
                  style={{
                    overscrollBehavior: "contain",
                    WebkitOverflowScrolling: "touch",
                  }}
                >
                  {children}

                  {stickyFooter && !full && (
                    <div className="sticky bottom-0 bg-white z-30">
                      <div className="relative p-4 pointer-events-auto">
                        <div className="pointer-events-none absolute -top-4 left-0 right-0 h-4 bg-gradient-to-t from-white to-transparent" />
                        {typeof stickyFooter === "function"
                          ? stickyFooter(handleClose)
                          : stickyFooter}
                      </div>
                    </div>
                  )}
                </div>

                {/* Close button inside modal for non-full modals (kept outside scroll container) */}
                {showCloseButton && !full && (
                  <button
                    aria-label="Close"
                    onClick={handleClose}
                    className="fixed right-4 top-4 md:right-6 md:top-6 p-2 rounded-full bg-gray-100 z-50 rtl:right-auto rtl:left-4 md:rtl:left-6"
                  >
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M6.29935 18.9077L5.1416 17.7L10.8166 12L5.1416 6.24999L6.29935 5.04224L12.0244 10.7922L17.6994 5.04224L18.8571 6.24999L13.1821 12L18.8571 17.7L17.6994 18.9077L12.0244 13.1577L6.29935 18.9077Z"
                        fill="#16181D"
                      />
                    </svg>
                  </button>
                )}
              </div>

              {/* For full modals, render close button outside the transformed container */}
              {showCloseButton && full && (
                <button
                  aria-label="Close"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleClose();
                  }}
                  className="fixed top-4 right-4 md:top-6 md:right-6 p-2 rounded-full bg-gray-100 z-[201] rtl:right-auto rtl:left-4 md:rtl:left-6"
                >
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M6.29935 18.9077L5.1416 17.7L10.8166 12L5.1416 6.24999L6.29935 5.04224L12.0244 10.7922L17.6994 5.04224L18.8571 6.24999L13.1821 12L18.8571 17.7L17.6994 18.9077L12.0244 13.1577L6.29935 18.9077Z"
                      fill="#16181D"
                    />
                  </svg>
                </button>
              )}

              {/* For full modals, render footer outside the transformed container */}
              {stickyFooter && full && (
                <div
                  className="fixed bottom-0 left-0 right-0 z-[201] pointer-events-none"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="w-full md:max-w-[480px] mx-auto bg-white pointer-events-auto">
                    <div className="relative p-4">
                      <div className="pointer-events-none absolute -top-4 left-0 right-0 h-4 bg-gradient-to-t from-white to-transparent" />
                      {typeof stickyFooter === "function"
                        ? stickyFooter(handleClose)
                        : stickyFooter}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </DialogPrimitive.Content>
        </DialogPortal>
      </Dialog>
    </ModalCloseContext.Provider>
  );

  return typeof window !== "undefined"
    ? createPortal(modalContent, document.body)
    : modalContent;
};

export const ModalHead = ({
  children,
  onClose,
  titleAlignment = "center",
}: {
  children: React.ReactNode;
  onClose: VoidFunction;
  titleAlignment?: "center" | "left";
}) => {
  const contextClose = useContext(ModalCloseContext);
  const clickClose = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (contextClose) contextClose();
    else onClose();
  };

  const titleClass =
    titleAlignment === "left"
      ? "text-xl text-left rtl:text-right md:text-2xl font-semibold text-gray-900 grow"
      : "text-xl text-center md:text-2xl font-semibold text-gray-900 grow";

  return (
    <div className="flex items-center justify-between mb-6 relative">
      <div className="fixed right-4 top-4 md:right-6 md:top-6 z-50 rtl:right-auto rtl:left-4 md:rtl:left-6">
        <button onClick={clickClose} className="p-2 rounded-full bg-gray-100">
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M6.29935 18.9077L5.1416 17.7L10.8166 12L5.1416 6.24999L6.29935 5.04224L12.0244 10.7922L17.6994 5.04224L18.8571 6.24999L13.1821 12L18.8571 17.7L17.6994 18.9077L12.0244 13.1577L6.29935 18.9077Z"
              fill="#16181D"
            />
          </svg>
        </button>
      </div>
      <h2 className={titleClass}>
        {children}
      </h2>
    </div>
  );
};

export default Modal;
